import {useCallback, useEffect, useState} from 'react';
import {fetchParkedCarts, PARKED_CARTS_CHANGED_EVENT} from '../infrastructure/parkedCartApi';
import {getOrElse, initial, isSuccess, pending} from '../../common/domain/RemoteData.model';
import {useUserContext} from '../../user/infrastructure/UserContext';

/**
 * Hook con los carritos guardados del usuario logueado (comercial o cliente normal).
 * Hasta que hay usuario cargado no llama al backend y devuelve lista vacía.
 *
 * A propósito NO depende del cliente representado (actingAsCliente): la lista es
 * de todo el usuario y el panel decide cómo agrupar/destacar por cliente.
 */
export const useParkedCarts = () => {
    const [parkedData, setParkedData] = useState(initial([]));
    const user = useUserContext();
    const enabled = !!user;

    const refresh = useCallback(async () => {
        if (!enabled) {
            return;
        }
        setParkedData(pending());
        const result = await fetchParkedCarts();
        setParkedData(result);
    }, [enabled]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Cualquier mutación (aparcar, recuperar, eliminar) dispara PARKED_CARTS_CHANGED_EVENT:
    // todas las instancias del hook se refrescan al momento, en silencio (sin pasar por
    // pending()) para que el badge no parpadee. Mismo patrón que usePendingOrders.
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const onChanged = async () => {
            const result = await fetchParkedCarts();
            setParkedData((current) => (isSuccess(result) ? result : current));
        };
        window.addEventListener(PARKED_CARTS_CHANGED_EVENT, onChanged);
        return () => window.removeEventListener(PARKED_CARTS_CHANGED_EVENT, onChanged);
    }, [enabled]);

    const parkedCarts = getOrElse(parkedData, []) ?? [];

    return {parkedData, parkedCarts, refresh};
};
